import {Injectable} from '@angular/core';
import * as XLSX from 'xlsx';
import {saveAs} from 'file-saver';
import {from, Observable, Subject} from 'rxjs';
import {groupBy, mergeMap, toArray} from 'rxjs/operators';
import {UtilService} from '../../common/services/util.service';
import {Common, isValidDateFormat, isValidNumberWithTwoDecimalPlaces} from '../../common/util/Util';

export enum ClaimColumn {
  InvoiceNo = 'invoiceNo*',
  MemberNo = 'memberNo*',
  DependentNo = 'dependentNo*',
  SubmissionDate = 'submissionDate*',
  AuthorisationNo = 'authorisationNo',
  ServiceDate = 'serviceDate*',
  ICD10Codes = 'icd10Codes*',
  TariffCode = 'tariffCode*',
  Quantity = 'quantity*',
  Amount = 'amount*',
}

const ErrorClaimColumnDisplay: any = {
  [ClaimColumn.InvoiceNo]: 'Invoice No',
  [ClaimColumn.MemberNo]: 'Member Number',
  [ClaimColumn.DependentNo]: 'Dependent Number ',
  [ClaimColumn.SubmissionDate]: 'Submission Date',
  [ClaimColumn.AuthorisationNo]: 'Authorisation Number',
  [ClaimColumn.ServiceDate]: 'Service Date',
  [ClaimColumn.ICD10Codes]: 'ICD10 Codes',
  [ClaimColumn.TariffCode]: 'Tariff Code',
  [ClaimColumn.Quantity]: 'Quantity Number',
  [ClaimColumn.Amount]: 'Amount',
};

export interface ExcelClaim {
  [ClaimColumn.InvoiceNo]: string;
  [ClaimColumn.MemberNo]: string;
  [ClaimColumn.DependentNo]: string;
  [ClaimColumn.SubmissionDate]: string;
  [ClaimColumn.AuthorisationNo]: string;
  [ClaimColumn.ServiceDate]: string;
  [ClaimColumn.ICD10Codes]: string;
  [ClaimColumn.TariffCode]: string;
  [ClaimColumn.Quantity]: string;
  [ClaimColumn.Amount]: string;
}

export interface ImportClaim {
  claim: ExcelClaim;
  isValid: boolean;
  validations: string[];
}

@Injectable({
  providedIn: 'root'
})
export class ClaimExcelSheetService {
  private readonly columns: string[] = Object.values(ClaimColumn);
  private readonly optionalCols: string[] = [ClaimColumn.AuthorisationNo];
  private readonly requiredCols: string[] = this.columns.filter((key: any) => !this.optionalCols.includes(key));
  private readonly workSheetName = 'claims_template';
  private readonly uploadClaim$: Subject<boolean> = new Subject<boolean>();
  public readonly uploadClaim$AsObservable: Observable<boolean> = this.uploadClaim$.asObservable();
  private _data: any = {};
  private _claimLines: ImportClaim[] = [];
  private _groupDataByInvoiceNo: ExcelClaim[][] = [[]];

  constructor(private utilService: UtilService) {}

  get data(): any {
    return this._data;
  }

  private set data(value: any) {
    this._data = value;
  }

  get claimLines(): ImportClaim[] {
    return this._claimLines;
  }

  set claimLines(value: ImportClaim[]) {
    this._claimLines = value;
  }

  get groupDataByInvoiceNo(): any {
    return this._groupDataByInvoiceNo;
  }

  private set groupDataByInvoiceNo(value: any) {
    this._groupDataByInvoiceNo = value;
  }

  clearData(): void {
    this.data = {};
    this.claimLines = [];
    this.groupDataByInvoiceNo = [[]];
  }

  uploadClaims(ev: Event): void {
    const fileInput = ev.target as HTMLInputElement;
    if (fileInput.files && fileInput.files.length > 0) {
      let workBook = null;
      let jsonData = null;
      const reader: FileReader = new FileReader();
      const file: File = fileInput.files[0];
      reader.onload = (event) => {
        const data = reader.result;
        workBook = XLSX.read(data, {type: 'array'});
        jsonData = workBook.SheetNames.reduce((initial, name) => {
          const sheet = workBook.Sheets[name];
          this.changeTypeOfColumnsToStr(sheet);
          initial[name] = XLSX.utils.sheet_to_json(sheet, {raw: false});
          return initial;
        }, {});

        this.clearData();
        this.setAndUpdateData(jsonData);
        this.uploadClaim$.next(true);
      };
      reader.readAsArrayBuffer(file);
    }
    else {
      // No file selected, clear the input value
      fileInput.value = '';
    }
  }

  downloadTemplate(): void {
    const headersCols = Object.values(ClaimColumn);
    const workbook = XLSX.utils.book_new();

    /** Converts an array of arrays of JS data to a worksheet. */
    const worksheet = XLSX.utils.aoa_to_sheet([headersCols]);

    XLSX.utils.book_append_sheet(workbook, worksheet, this.workSheetName);
    const excelSheet = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'});
    const blob: Blob = new Blob([excelSheet], {
      type: Common.MIME_TYPE_XLSX
    });
    const filename = `claim_upload_template_${this.utilService.dateTimeFormat()}`;
    saveAs(blob, `${filename}.xlsx`);
  }

  private changeTypeOfColumnsToStr(worksheet): void {
    // Loop through all cells in the worksheet and set the cell type to 's' (text)
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let rowNum = range.s.r; rowNum <= range.e.r; rowNum++) {
      for (let colNum = range.s.c; colNum <= range.e.c; colNum++) {
        const cellAddress = XLSX.utils.encode_cell({r: rowNum, c: colNum});
        if (worksheet[cellAddress]) {
          worksheet[cellAddress] = {v: worksheet[cellAddress].w.toString(), t: 's'};
        }
      }
    }
  }

  private setAndUpdateData(data: any): void {
    this.data = data[this.workSheetName];
    this.groupDataByInvoiceNo = this.groupRowsByInvoiceNo(this.data);
    this.checkAndSetInvalidRowsValidation();
  }

  private checkAndSetInvalidRowsValidation(): void {
    const data: ExcelClaim[] = this.data;
    for (let i = 0; i < data.length; i++) {
      const row: ExcelClaim = data[i];
      const validations = this.validateColumns(data[i]);
      const invalidClaim: ImportClaim = {claim: row, validations, isValid: validations?.length === 0};
      this.claimLines.push(invalidClaim);
    }
  }

  private validateColumns(row: ExcelClaim): any {
    const rowValidations = [];
    const dateFields = [ClaimColumn.SubmissionDate, ClaimColumn.ServiceDate,];
    const doubleFields = [ClaimColumn.Quantity, ClaimColumn.Amount];
    this.validateRequiredFields(row, this.requiredCols, rowValidations);
    this.validateDateFields(row, dateFields, rowValidations);
    this.validateDoubleFields(row, doubleFields, rowValidations);
    this.validateSpecificFields(row, ClaimColumn.ICD10Codes, rowValidations);
    return rowValidations;
  }

  private validateRequiredFields(row, requiredKeys, rowValidations) {
    requiredKeys.forEach(key => {
      if (!row[key]) {
        rowValidations.push(`${ErrorClaimColumnDisplay[key]} is required`);
      }
    });
  }

  private validateDateFields(row, dateFields, rowValidations) {
    dateFields.forEach(key => {
      if (!isValidDateFormat(row[key])) {
        rowValidations.push(`${ErrorClaimColumnDisplay[key]} invalid date format e.g yyyy/MM/dd`);
      }
    });
  }

  private validateSpecificFields(row, field, rowValidations) {
    if (field === ClaimColumn.ICD10Codes) {
      const value = row[field];
      const containsSpecialChar = this.containsSpecialCharacter(value);

      if (containsSpecialChar) {
        rowValidations.push(`ICD10Codes has invalid character. Only ";" is allowed`);
      }
    }
  }

  private containsSpecialCharacter(value: string): boolean {
    const notAllowedChars = ['!', '#', '$', '&', '*', '+', '-', ':', '<', '=', '>', '?', '@'];
    for (const char of notAllowedChars) {
      if (value && value.includes(char)) {
        return true; // String contains at least one special character
      }
    }
    return false; // String does not contain any special character
  }

  private validateDoubleFields(row, doubleFields, rowValidations) {
    doubleFields.forEach(key => {
      if (!isValidNumberWithTwoDecimalPlaces(row[key])) {
        rowValidations.push(`${ErrorClaimColumnDisplay[key]} invalid number format. e.g .00`);
      }
    });
  }

  private groupRowsByInvoiceNo(data: ExcelClaim[]): ExcelClaim[][] {
    let groupedData: any[] = [];
    from(data)
      .pipe(
        groupBy((item: ExcelClaim) => {
          return item[ClaimColumn.InvoiceNo]; // Group the items by the 'invoiceNo' property as it will be UNIQUE
        }),
        mergeMap(group$ =>
          group$.pipe(
            toArray()
          )
        ),
        toArray()
      )
      .subscribe((response: ExcelClaim[][]) => {
        groupedData = response;
      });
    return groupedData;
  }
}
